import { Box, Checkbox, Chip, IconButton, Typography, alpha } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PermissionTreeNode as PermissionTreeNodeType, AnnotatedFeature } from 'types/apis/rbacInterfaces';

// Pending change keys: `g:<groupId>` for feature groups, `f:<featureId>` for features.
// A key's absence means "no pending change" — the server state applies.

export const isFeatureEffectivelyGranted = (
  feature: AnnotatedFeature,
  pending: Map<string, boolean>,
  ancestorPendingGrant: boolean,
  ancestorPendingRevokeIds: Set<number>,
  ancestorPendingRevoke = false,
): boolean => {
  const override = pending.get(`f:${feature.id}`);
  if (override !== undefined) return override;
  if (ancestorPendingGrant) return true;
  if (ancestorPendingRevoke) return false;
  if (
    feature.granted_via_group_id != null &&
    ancestorPendingRevokeIds.has(feature.granted_via_group_id) &&
    !feature.granted_directly
  ) {
    return false;
  }
  return feature.grant_state === 'granted';
};

export const countSubtreeFeatures = (
  node: PermissionTreeNodeType,
  pending: Map<string, boolean>,
  ancestorPendingGrant: boolean,
  ancestorPendingRevokeIds: Set<number>,
): { granted: number; total: number } => {
  const override = pending.get(`g:${node.id}`);
  const selfPendingGrant = ancestorPendingGrant || override === true;
  // A pending revoke on this group only affects its own features — child groups
  // carry their own override (the toggle cascade writes one per descendant), so
  // a child the user re-checked keeps counting as granted.
  const selfPendingRevoke = override === false;
  const revokeIds = override === false
    ? new Set([...ancestorPendingRevokeIds, node.id])
    : ancestorPendingRevokeIds;

  let granted = 0;
  let total = 0;
  (node.features || []).forEach((feature) => {
    total += 1;
    if (isFeatureEffectivelyGranted(feature, pending, selfPendingGrant, revokeIds, selfPendingRevoke)) granted += 1;
  });
  (node.children || []).forEach((child) => {
    const counts = countSubtreeFeatures(child, pending, selfPendingGrant, revokeIds);
    granted += counts.granted;
    total += counts.total;
  });
  return { granted, total };
};

// Toggling a group applies the same pending grant/revoke to every descendant group,
// so the checkbox cascade, subtree counts, and the saved API calls all stay in sync.
export const cascadeGroupToggle = (
  node: PermissionTreeNodeType,
  grant: boolean,
  onToggleGroup: (groupId: number, grant: boolean, serverGranted: boolean) => void,
) => {
  onToggleGroup(node.id, grant, node.grant_state === 'granted');
  (node.children || []).forEach((child) => cascadeGroupToggle(child, grant, onToggleGroup));
};

const pendingRowBg = { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.08) };
const chipSx = { height: 18, fontSize: 11 } as const;

interface PermissionTreeNodeProps {
  node: PermissionTreeNodeType;
  depth: number;
  pending: Map<string, boolean>;
  collapsed: Set<number>;
  searchActive: boolean;
  ancestorPendingGrant?: boolean;
  ancestorPendingRevokeIds?: Set<number>;
  onToggleExpand: (id: number) => void;
  onToggleGroup: (groupId: number, grant: boolean, serverGranted: boolean) => void;
  onToggleFeature: (featureId: number, grant: boolean, serverGranted: boolean) => void;
}

const EMPTY_REVOKE_IDS = new Set<number>();

export const PermissionTreeNode = ({
  node,
  depth,
  pending,
  collapsed,
  searchActive,
  ancestorPendingGrant = false,
  ancestorPendingRevokeIds = EMPTY_REVOKE_IDS,
  onToggleExpand,
  onToggleGroup,
  onToggleFeature,
}: PermissionTreeNodeProps) => {
  const hasChildren = (node.children?.length || 0) > 0 || (node.features?.length || 0) > 0;
  const expanded = searchActive || !collapsed.has(node.id);

  const groupOverride = pending.get(`g:${node.id}`);
  const serverGranted = node.grant_state === 'granted';
  const selfPendingGrant = ancestorPendingGrant || groupOverride === true;
  const selfPendingRevoke = groupOverride === false;
  const childRevokeIds = groupOverride === false
    ? new Set([...ancestorPendingRevokeIds, node.id])
    : ancestorPendingRevokeIds;

  const counts = countSubtreeFeatures(node, pending, ancestorPendingGrant, ancestorPendingRevokeIds);
  // The group checkbox is a pure tri-state aggregate of its subtree: all
  // granted → checked, some → indeterminate, none → empty. The group's own
  // grant only decides the display when there is nothing underneath to count.
  const selfChecked = groupOverride !== undefined ? groupOverride : ancestorPendingGrant || serverGranted;
  const checked = counts.total > 0 ? counts.granted === counts.total : selfChecked;
  const indeterminate = counts.granted > 0 && counts.granted < counts.total;
  const groupIsPending = groupOverride !== undefined;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: depth * 2,
          py: 0.25,
          borderRadius: 1,
          ...(groupIsPending ? pendingRowBg : {}),
        }}
      >
        <IconButton
          size="small"
          onClick={() => onToggleExpand(node.id)}
          disabled={!hasChildren}
          sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          disabled={ancestorPendingGrant}
          onChange={(e) => cascadeGroupToggle(node, e.target.checked, onToggleGroup)}
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{node.name}</Typography>
        {counts.total > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${counts.granted}/${counts.total}`}
            sx={{ ...chipSx, color: 'text.secondary' }}
          />
        )}
        {groupIsPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
      </Box>

      {expanded && (
        <Box>
          {node.children?.map((child) => (
            <PermissionTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              pending={pending}
              collapsed={collapsed}
              searchActive={searchActive}
              ancestorPendingGrant={selfPendingGrant}
              ancestorPendingRevokeIds={childRevokeIds}
              onToggleExpand={onToggleExpand}
              onToggleGroup={onToggleGroup}
              onToggleFeature={onToggleFeature}
            />
          ))}
          {node.features?.map((feature) => {
            const override = pending.get(`f:${feature.id}`);
            const featureServerGranted = feature.grant_state === 'granted';
            const featureChecked = isFeatureEffectivelyGranted(feature, pending, selfPendingGrant, childRevokeIds, selfPendingRevoke);
            const inheritedFromGroup = !feature.granted_directly && !!feature.granted_via_group_id
              && !(feature.granted_via_group_id != null && childRevokeIds.has(feature.granted_via_group_id));
            const pendingViaGroup = selfPendingGrant && override === undefined;
            const viaRole = !feature.granted_directly && !!feature.granted_via_role_id;
            const featureIsPending = override !== undefined;

            return (
              <Box
                key={feature.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: (depth + 1) * 2 + 4,
                  py: 0.25,
                  borderRadius: 1,
                  ...(featureIsPending ? pendingRowBg : {}),
                }}
              >
                <Checkbox
                  size="small"
                  checked={featureChecked}
                  disabled={inheritedFromGroup || pendingViaGroup}
                  onChange={(e) => onToggleFeature(feature.id, e.target.checked, featureServerGranted)}
                />
                <Typography variant="body2" color="text.secondary">{feature.label || feature.endpoint}</Typography>
                {inheritedFromGroup && (
                  <Chip size="small" variant="outlined" label="via group" sx={{ ...chipSx, color: 'text.secondary' }} />
                )}
                {pendingViaGroup && !inheritedFromGroup && (
                  <Chip size="small" variant="outlined" color="primary" label="via group · pending" sx={chipSx} />
                )}
                {viaRole && (
                  <Chip size="small" variant="outlined" label="via role" sx={{ ...chipSx, color: 'text.secondary' }} />
                )}
                {featureIsPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
